import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule, Routes } from '@angular/router';
import { DataGridComponent, NoComponentComponent } from '@fitness-force/grid';
import { Campaign, TENANTSINTERFACE } from '@fitness-force/models';
import { AgeCellComponent, StaffInfoComponent } from '@fitness-force/shared';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TENANTS_INFO } from 'apps/ff-frontend/src/app/state/app.state';
import { CampaignService } from 'libs/api/src/lib/campaign/campaign.service';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';

import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { EditorModule } from 'primeng/editor';
import { FileUploadModule } from 'primeng/fileupload';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';

import { OverlayModule } from 'primeng/overlay';
import { PopoverModule } from 'primeng/popover';
import { StyleClassModule } from 'primeng/styleclass';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { BehaviorSubject, Observable } from 'rxjs';
import { CampaignStatusComponent } from '../campaign-status/campaign-status.component';
import { CampaignNameComponent } from '../campaign-name/campaign-name.component';
import { SkeletonModule } from 'primeng/skeleton';
import { ActionsComponent } from '@fitness-force/grid-utility/action';
import { ToastMessageService } from 'libs/api/src/lib/toastmessage/toastmessage.service';
import { ConfirmationService, ConfirmEventType } from 'primeng/api';

@Component({
  selector: 'fitness-force-campaign-list',
  templateUrl: './campaign-list.component.html',
  styleUrls: ['./campaign-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ToggleSwitchModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    DatePickerModule,
    SelectModule,
    EditorModule,
    ButtonModule,
    FileUploadModule,
    OverlayModule,
    PopoverModule,
    CheckboxModule,
    TableModule,
    AvatarModule,
    StyleClassModule,
    TagModule,
    TagModule,
    AvatarModule,
    StyleClassModule,
    DataGridComponent,
    SkeletonModule,
    TranslateModule,
    RouterModule
]
})
export class CampaignListComponent implements OnInit {
  getAccessTenantList$: Observable<any> = new Observable();
  tenantId = 0;
  tenant!: any;
  showSkeleton$ = new BehaviorSubject(true);
  selectAllData = new BehaviorSubject(false);
  emptyStateComponent$ = new BehaviorSubject<any>(NoComponentComponent);
  totalRecordLength = new BehaviorSubject({
    totalPage: 0,
    totalItems: 0,
    currentPage: 0,
    reset:false
  });
  tableData: any = new BehaviorSubject([]);
  campaignsList = new BehaviorSubject<any[]>([]);
  columns: any = new BehaviorSubject([]);
  loading = new BehaviorSubject<boolean>(false);
  staffList:any;
  Params: any = new Map();
  searchText: string = '';
  filters: any[]=[
    {name: 'ALL', value:'all'},
    {name: 'RECENT',value:'recent'},
    {name: 'PAST',value:'past'},
  ];
  selectedFilter: {name: string, value:string} | undefined;
  
  constructor(
    private store: Store,
    public translate: TranslateService,
    private campaignService: CampaignService,
    private toastMessageService: ToastMessageService,
    private router: Router,
    private confirmationService: ConfirmationService
  ){
    this.filters = [
      {name: 'ALL', value:'all'},
      {name: 'RECENT',value:'recent'},
      {name: 'PAST',value:'past'},
      ];
  }

  ngOnInit(): void {
    this.showSkeleton$.next(true); 
    this.selectedFilter = this.filters.find(o => o.value == 'recent');
    this.Params.set('PageNumber', { name: 'PageNumber', value: 1 });
    this.Params.set('PageSize', { name: 'PageSize', value: 10 });
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);
    this.getAccessTenantList$.subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantId = tenant.selected_tenant.id;
      if (this.tenantId > 0) {
        this.tenant = tenant.selected_tenant;
        this.getCampaignList();
      }
    });
    }

    getCampaignList() {
      this.loading.next(true);
      this.campaignService.GetCampaignList(this.tenantId, [...this.getParams])
        .subscribe({
          next: (response: any) => {
            let data = response.items as Campaign[];
            if (data) {
              data.forEach((item: any) => {
                item.status = item.campaign_status;
              });
              this.campaignsList.next(data);
              this.filterData(this.selectedFilter?.value);
              this.columnTranslate();
              this.totalRecordLength.next({ 
                totalPage: response.total_pages,
                totalItems: response.total_items,
                currentPage: response.current_page,
                reset:false
              });
              this.loading.next(false);
            } else {
              this.tableData.next([]);
              this.columns.next([]);
              this.loading.next(false);
            }
          },
          error: () => {
            this.tableData.next([]);
            this.columns.next([]);
            this.loading.next(false);
          },
        });
    }

    columnTranslate() {
      this.translate
        .get([
          'CAMPAIGN.TABLE.CAMPAIGN_NAME',
          'CAMPAIGN.TABLE.CREATED_ON',
          'CAMPAIGN.TABLE.CREATED_BY',
          'CAMPAIGN.TABLE.STATUS'
        ])
        .subscribe((translations) => {
          this.setColumnTranslate(translations);
        });
    }

    setColumnTranslate(translations: any) {
      let columns ={
        Name: translations['CAMPAIGN.TABLE.CAMPAIGN_NAME'],
        CreatedOn: translations['CAMPAIGN.TABLE.CREATED_ON'],
        CreatedBy: translations['CAMPAIGN.TABLE.CREATED_BY'],
        Status: translations['CAMPAIGN.TABLE.STATUS'],
      };
      this.columns.next([
        {
          title: columns.Name,
          classes: ' py-2  white-space-nowrap relative font-bold ',
          sortable: true,
          filterable: true,
          dataProperty: 'campaign_name',
          type: 'component',
          data: {
            name:CampaignNameComponent,
            fetch_key: 'campaign_name',
            additionData: true,
            aData: {
              is_campaign:true,
              keyName: 'campaign_name',
              keyName1: 'campaign_description',
              uri: '/campaign/campaign-details',
            },
          },
        },
        {
          title: columns.CreatedOn,
          classes: ' py-2  white-space-nowrap',
          sortable: true,
          filterable: true,
          dataProperty: 'created_date',
          type: 'component',
          data: {
            name: AgeCellComponent,
            additionData: true,
            aData: { fetch_key: 'created_date' },
          },
        },
        {
          title: columns.CreatedBy,
          classes: ' py-2  white-space-nowrap capitalize',
          sortable: true,
          filterable: true,
          dataProperty: 'created_by',
          type: 'component',
          data: { name: StaffInfoComponent },
        },
        {
          title: columns.Status,
          classes: 'py-2  white-space-nowrap',
          sortable: false,
          filterable: true,
          dataProperty: 'campaign_status',
          type: 'component',
          data: {
            name: CampaignStatusComponent,
            additionData: true,
            aData: { fetch_key: 'campaign_status',
            is_campaign:true },
          },
        },
        {
          title: '',
          classes: ' py-2  white-space-nowrap',
          dataProperty: 'actions',
          type: 'component',
          data: {
            name: ActionsComponent,
            additionData: true,
            aData: ['status','duplicate','void_campaign','stop_campaign'],
          },
        },
      ]);
    }

    fetchNextSetData(e: any) {
      this.Params.set('PageNumber', { name: 'PageNumber', value: e.pn });
      this.Params.set('PageSize', { name: 'PageSize', value: e.ps });
      this.getCampaignList();
    }

    get getParams(): Array<any> {
      return [...this.Params.values()];
    }

    get filteredData() {
      let tableData= this.tableData.value;
      return new BehaviorSubject(tableData.filter((item: { campaign_name: string; }) =>
      item.campaign_name.toLowerCase().includes(this.searchText.toLowerCase())
      ))
    }

    onActionEvent(event:any)
    {
      switch (event.action) {
        case 'status':
          this.toggleStatus(event.columnData);
          break;
          case 'duplicate':
            this.router.navigate(['/campaign/create-camapaign', event.columnData.campaign_id]);
          break;
        case 'stop_campaign':
          this.confirmDialog('stop_campaign',event.columnData);
          break;
        case 'void_campaign':
          this.confirmDialog('void_campaign',event.columnData);
          break;    
      }
    }
    

    toggleStatus(event:any)
    {
      console.log(event);
      if(event.status.toLowerCase() == 'active')
      {
        this.campaignService.updateCampaignStatus(event.guid, 'inactive').subscribe({
          next: () => {
            this.translate.get('CAMPAIGN.MARK_AS_INACTIVE').subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
              this.getCampaignList();
            });
          },
          error: (error:any) => {
            this.toastMessageService.showError(error);
          }
        });
      }
      else{
        this.campaignService.updateCampaignStatus(event.guid, 'active').subscribe({
          next: () => {
            this.translate.get('CAMPAIGN.MARK_AS_ACTIVE').subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
              this.getCampaignList();
            });
          },
          error: (error:any) => {
            this.toastMessageService.showError(error);
          }
        });
      }
    }

    selectFilter(event:any)
    {
      this.selectedFilter = this.filters.find(o => o.value == event.value.value);
      this.filterData(event.value.value);
    }

    filterData(selectedFilter:any) {
      const today = new Date();
      const tenDaysAgo = new Date(today);
      tenDaysAgo.setDate(today.getDate() - 10);

      switch (selectedFilter) {
        case 'all':
          this.tableData.next(this.campaignsList.value);
          break;
        case 'recent':
          const recentCampaigns = this.campaignsList.value.filter(campaign => {
            const updatedDate = new Date(campaign.updated_date);
            
            // Recent campaigns include:
            // 1. Active status
            // 2. Scheduled status
            // 3. inprogress status
            // 4. Completed status
            // 5. void status within last 10 days
            // 6. Stopped status within last 10 days
            return (
              campaign.campaign_status.toLowerCase() === 'active' ||
              campaign.campaign_status.toLowerCase() === 'scheduled' ||
              campaign.campaign_status.toLowerCase() === 'inprogress' ||
              campaign.campaign_status.toLowerCase() === 'completed' ||
              (campaign.campaign_status.toLowerCase() === 'void' && updatedDate >= tenDaysAgo) ||
              (campaign.campaign_status.toLowerCase() === 'stopped' && updatedDate >= tenDaysAgo)
            );
          });
          this.tableData.next(recentCampaigns);
          break;
        case 'past':
          const pastCampaigns = this.campaignsList.value.filter(campaign => {
            const updatedDate = new Date(campaign.updated_date);
            
            // Past campaigns include:
            // 1. Inactive status
            // 2. Archived status
            // 3. void status older than 10 days
            // 4. Stopped status older than 10 days
            return (
              campaign.campaign_status.toLowerCase() === 'inactive' ||
              campaign.campaign_status.toLowerCase() === 'archived' ||
              (campaign.campaign_status.toLowerCase() === 'void' && updatedDate < tenDaysAgo) ||
              (campaign.campaign_status.toLowerCase() === 'stopped' && updatedDate < tenDaysAgo)
            );
          });
          this.tableData.next(pastCampaigns);
          break;
      }
    }

    confirmDialog(action:string,columnData:any) {
      let message = '';
      switch (action) {
        case 'stop_campaign':
          message = this.translate.instant('CAMPAIGN.STOP_CONFIRM_MSG');
          break;
        case 'void_campaign':
          message = this.translate.instant('CAMPAIGN.VOD_CONFIRM_MSG');
          break;
      }
      this.confirmationService.confirm({
          message: message,
          icon: 'fa fa-exclamation-triangle',
          accept: () => {
            switch (action) {
              case 'stop_campaign':
                this.stopCampaign(columnData);
                break;
              case 'void_campaign':
                this.voidCampaign(columnData);
                break;
            }
          },
          reject: (type: any) => {
            switch (type) {
              case ConfirmEventType.REJECT:
                break;
              case ConfirmEventType.CANCEL:
                break;
            }
          }
        });
    }

    stopCampaign(columnData:any) {
      this.campaignService.stopCampaign(columnData.guid).subscribe({
        next: () => {
          this.translate.get('CAMPAIGN.STOP_CAMPAIGN').subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
            this.getCampaignList();
          });
        },
        error: (error:any) => {
         this.toastMessageService.showError(error);
        }
      });
    }

    voidCampaign(columnData:any) {
      this.campaignService.voidCampaign(columnData.guid).subscribe({
        next: () => {
          this.translate.get('CAMPAIGN.VOID_CAMPAIGN').subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
            this.getCampaignList();
          });
        },
        error: (error:any) => {
          this.toastMessageService.showError(error);
        }
      });
    }

  }

